import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getStoredToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MeAIContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function MeAIProvider({ children }) {
  const { user } = useAuth();
  const [being, setBeing] = useState(null);

  const fetchBeing = useCallback(async () => {
    try {
      const { data } = await api.get("/me/being");
      setBeing(data);
      return data;
    } catch {
      setBeing(null);
      return null;
    }
  }, []);

  // Initial fetch + lightweight polling (every 8s) so handovers from
  // other devices propagate even if the WS upgrade is blocked.
  useEffect(() => {
    if (!user || typeof user !== "object") {
      setBeing(null);
      return;
    }
    fetchBeing();
    const t = setInterval(fetchBeing, 8000);
    return () => clearInterval(t);
  }, [user, fetchBeing]);

  // Real-time WebSocket — handover & presence events.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const token = getStoredToken();
    if (!token || !BACKEND_URL) return;

    let ws = null;
    try {
      const url = BACKEND_URL.replace(/^http/, "ws") + `/api/me/ws?token=${encodeURIComponent(token)}`;
      ws = new WebSocket(url);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "snapshot" && msg.being) setBeing(msg.being);
          if (msg.type === "handover" || msg.type === "release") fetchBeing();
        } catch {
          // ignore malformed payloads
        }
      };
    } catch {
      // ignore — polling continues to work
    }
    return () => {
      if (ws) try { ws.close(); } catch { /* noop */ }
    };
  }, [user, fetchBeing]);

  async function claim(deviceId) {
    const { data } = await api.post(`/devices/${deviceId}/claim`);
    setBeing(data);
    return data;
  }

  async function release() {
    await api.post("/me/being/release");
    await fetchBeing();
  }

  return (
    <MeAIContext.Provider value={{ being, refresh: fetchBeing, claim, release }}>
      {children}
    </MeAIContext.Provider>
  );
}

export function useMeAI() {
  const ctx = useContext(MeAIContext);
  if (!ctx) throw new Error("useMeAI must be used inside MeAIProvider");
  return ctx;
}
